import React, {
  useState,
  useContext,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useCallback,
  useMemo,
} from "react";
import { EstimateContext } from "../Estimate";
import { useEstimateState } from "../EstimateContext";
import { calculateConstructionDesign } from "../../../../../api/projectEstimatesApi";
import {
  ModuleBonusDayCells,
  ModuleBonusMoneyCells,
  ModuleCorrectionRows,
  ModuleDirectMoneyCells,
} from "../ModuleCorrectionRows";
import {
  formatSignedDelta,
  getModuleCorrectionFooter,
} from "../correctionStorage";
import "./BimTab.css";

const BIM_MANAGER_ROLE = "BIM менеджер";

// Сравнение без учёта регистра: API может вернуть "BIM Менеджер", в коде — "BIM менеджер"
const isBimManagerRole = (r) =>
  r?.role?.trim().toLowerCase() === BIM_MANAGER_ROLE.toLowerCase();

const defaultBimManagerRole = () => ({
  role: BIM_MANAGER_ROLE,
  average_salary: 0,
  day_efforts: { direct: 0, bonus: 0 },
  money_expenditures: { direct: 0, bonus: 0 },
});

const BimTab = forwardRef(({ isEditable = true }, ref) => {
  const estimateContext = useContext(EstimateContext);
  const {
    getSectionData,
    updateEstimateSection,
    estimateData,
    sectionDetails: contextSections,
    setEstimateData,
    performSave,
  } = useEstimateState();

  // Получаем данные BIM координации из нового контекста состояния
  const bimCoordinationData = useMemo(() => {
    return getSectionData("bim_coordination") || {};
  }, [getSectionData]);

  const corrections = estimateContext?.corrections || [];
  const isCorrectionMode = Boolean(estimateContext?.isCorrectionMode);
  const canEditCorrections = Boolean(estimateContext?.canEditCorrections);
  const canEditBase = isEditable && !isCorrectionMode;
  const updateModuleCorrectionValue =
    estimateContext?.updateModuleCorrectionValue;

  // Постоянная колонка — только "BIM менеджер", остальные добавляются с РП. Порядок по order_index.
  const displayRoles = useMemo(() => {
    const fromApi = bimCoordinationData.roles || [];
    const sorted = [...fromApi].sort(
      (a, b) => (a.order_index ?? 0) - (b.order_index ?? 0),
    );
    const bimManager =
      sorted.find(isBimManagerRole) || defaultBimManagerRole();
    const others = sorted.filter((r) => !isBimManagerRole(r));
    return [bimManager, ...others];
  }, [bimCoordinationData.roles]);

  // Разделы с вкладки "Рабочее проектирование" — только они доступны для добавления в BIM
  const workTabSections = useMemo(() => {
    const list = [];
    const seen = new Set();
    const add = (arr) => {
      (arr || []).forEach((item) => {
        const id = item?.section?.id;
        const name = item?.section?.name;
        if (id != null && name != null && !seen.has(id)) {
          seen.add(id);
          list.push({
            id,
            name,
            description: item?.section?.description ?? "",
          });
        }
      });
    };
    add(estimateData?.construction_design);
    add(estimateData?.construction_design_project);
    return list;
  }, [
    estimateData?.construction_design,
    estimateData?.construction_design_project,
  ]);

  // Функция для извлечения ставок специалистов из API данных (по displayRoles)
  const getBimRoleSalariesFromApi = useCallback(() => {
    return displayRoles.map((role) => ({
      role: role.role,
      average_salary: role.average_salary,
      section_id: role.section_id ?? null,
    }));
  }, [displayRoles]);

  // Получаем данные из API или используем значения по умолчанию (для совместимости)
  const roles = displayRoles;
  // Используем данные с сервера для include_bonuses
  const includeBonuses = bimCoordinationData.include_bonuses ?? true;
  const qualityControlPercent = bimCoordinationData.quality_control_percent;
  const directBonusPercent = bimCoordinationData.direct_bonus_percent;

  // Состояния для редактируемых полей
  const [editingFields, setEditingFields] = useState({});
  const [savingFields, setSavingFields] = useState({});
  const [localValues, setLocalValues] = useState({
    include_bonuses: includeBonuses,
    quality_control_percent: qualityControlPercent,
    direct_bonus_percent: directBonusPercent,
    bim_role_salaries: [],
  });

  // Модалка добавления раздела в BIM: только разделы из Рабочего проектирования
  const [showAddBimSectionModal, setShowAddBimSectionModal] = useState(false);
  const [selectedBimSections, setSelectedBimSections] = useState([]);
  const [bimSectionSearch, setBimSectionSearch] = useState("");

  // Состояние для отслеживания загрузки и ошибок
  const [loading, setLoading] = useState(false);

  // Получаем projectId из URL
  const projectId = window.location.pathname.split("/").pop();

  // Инициализируем локальные значения при первой загрузке данных
  useEffect(() => {
    // Проверяем, есть ли активные поля в редактировании
    const hasActiveEditing = Object.values(editingFields).some(
      (isEditing) => isEditing,
    );
    const hasActiveSaving = Object.values(savingFields).some(
      (isSaving) => isSaving,
    );

    if (hasActiveEditing || hasActiveSaving) {
      return;
    }

    const newValues = {
      include_bonuses: bimCoordinationData.include_bonuses ?? true,
      quality_control_percent: bimCoordinationData.quality_control_percent,
      direct_bonus_percent: bimCoordinationData.direct_bonus_percent,
      bim_role_salaries: getBimRoleSalariesFromApi(),
    };

    setLocalValues(newValues);
  }, [
    bimCoordinationData,
    editingFields,
    savingFields,
    getBimRoleSalariesFromApi,
  ]);

  // Определяем колонки на основе ролей из API
  const bimColumns = roles.map((role) => role.role) || [];

  // Обработчики для редактируемых полей
  const handleEditField = (fieldName, value) => {
    setEditingFields((prev) => ({ ...prev, [fieldName]: true }));
    setLocalValues((prev) => ({ ...prev, [fieldName]: value }));
  };

  // Функция для калькуляции BIM данных (overrideEstimateData — при добавлении разделов)
  const calculateBimData = async (updatedBimData, overrideEstimateData) => {
    try {
      setLoading(true);

      const currentEstimateData = overrideEstimateData || estimateData || {};
      const currentSectionsData = contextSections || {};

      // Логируем только секцию 34 для отладки

      // Создаем обновленные данные для калькуляции

      const updatedEstimateData = {
        ...currentEstimateData,
        bim_coordination: {
          ...currentEstimateData.bim_coordination,
          // Обновляем только базовые параметры, сохраняя результаты расчетов
          include_bonuses: updatedBimData.include_bonuses,
          average_salary: updatedBimData.average_salary,
          quality_control_percent: updatedBimData.quality_control_percent,
          direct_bonus_percent: updatedBimData.direct_bonus_percent,
          // Обновляем только ставки в roles, сохраняя остальные данные
          roles:
            currentEstimateData.bim_coordination?.roles?.map(
              (existingRole, index) => {
                const updatedRole = updatedBimData.bim_role_salaries?.[index];
                if (updatedRole) {
                  return {
                    ...existingRole,
                    average_salary: updatedRole.average_salary,
                  };
                }
                return existingRole;
              },
            ) || [],
        },
      };

      // Обновляем контекст с новыми данными BIM перед калькуляцией
      setEstimateData(updatedEstimateData);

      // Вызываем калькуляцию
      const calculationResult = await calculateConstructionDesign(
        projectId,
        updatedEstimateData,
        currentSectionsData,
      );

      // Обновляем данные в контексте
      if (estimateContext && estimateContext.updateHeaderData) {
        const headerData = {
          contract_value: calculationResult.contract_value,
          contract_value_before_nds:
            calculationResult.contract_value_before_nds,
          contract_value_construction:
            calculationResult.contract_value_construction,
          contract_value_construction_percent:
            calculationResult.contract_value_construction_percent,
          cost_price: calculationResult.cost_price,
          cost_price_percent: calculationResult.cost_price_percent,
          difference: calculationResult.difference,
          nds_value: calculationResult.nds_value,
          indirect_expenditures: calculationResult.indirect_expenditures,
          indirect_expenditures_percent:
            calculationResult.indirect_expenditures_percent,
          pure_income: calculationResult.pure_income,
          pure_income_percent: calculationResult.pure_income_percent,
          net_profit: calculationResult.net_profit,
          net_profit_percent: calculationResult.net_profit_percent,
        };

        estimateContext.updateHeaderData(headerData);
      }

      // Обновляем данные в контексте: сохраняем наш список ролей (после удаления колонки
      // бэк может вернуть старый полный список), подставляем только рассчитанные поля из ответа
      if (calculationResult && calculationResult.bim_coordination) {
        const rolesWeSent =
          updatedBimData.bim_role_salaries ??
          updatedEstimateData.bim_coordination?.roles ??
          [];
        const rolesFromApi =
          calculationResult.bim_coordination.roles ?? [];
        const mergedRoles = rolesWeSent.map((ourRole) => {
          const fromApi = rolesFromApi.find(
            (r) => r.role === ourRole.role,
          );
          const merged = fromApi
            ? { ...fromApi, ...ourRole }
            : ourRole;
          // Приоритет section_id из ответа API (бэкенд теперь везде отдаёт section_id)
          merged.section_id = fromApi?.section_id ?? ourRole.section_id ?? null;
          return merged;
        });
        const mergedBimCoordination = {
          ...calculationResult.bim_coordination,
          roles: mergedRoles,
        };
        updateEstimateSection(
          "bim_coordination",
          mergedBimCoordination,
        );
      }

      // Обновляем локальные значения с результатами калькуляции
      if (calculationResult && calculationResult.bim_coordination) {
        const bc = calculationResult.bim_coordination;
        setLocalValues((prev) => ({
          ...prev,
          quality_control_percent:
            bc.quality_control_percent ?? prev.quality_control_percent,
          direct_bonus_percent:
            bc.direct_bonus_percent ?? prev.direct_bonus_percent,
          // Не обновляем bim_role_salaries здесь, так как UI использует данные из контекста
        }));
      }

      return calculationResult;
    } catch (error) {
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Обработчик для изменения чекбокса
  const handleCheckboxChange = async (fieldName, checked) => {
    setLocalValues((prev) => ({ ...prev, [fieldName]: checked }));

    try {
      // Подготавливаем данные для калькуляции
      const updatedBimData = {
        include_bonuses: checked,
        average_salary:
          localValues.bim_role_salaries?.[0]?.average_salary ||
          getSectionData("bim_coordination")?.average_salary,
        quality_control_percent: localValues.quality_control_percent,
        direct_bonus_percent: localValues.direct_bonus_percent,
        bim_role_salaries: localValues.bim_role_salaries,
      };

      // Вызываем калькуляцию
      await calculateBimData(updatedBimData);

      // Сохраняем состояние чекбокса локально
      try {
        localStorage.setItem(
          `estimate:${projectId}:bim_coordination:include_bonuses`,
          String(checked),
        );
      } catch (e) {
        // noop
      }
    } catch (error) {}
  };

  // Обработчик для сохранения изменений в ставке специалиста
  const handleSaveRoleSalary = async (roleIndex, newSalary) => {
    setSavingFields((prev) => ({
      ...prev,
      [`role_salary_${roleIndex}`]: true,
    }));

    try {
      // Обновляем значение в локальном состоянии
      const updatedRoleSalaries = localValues.bim_role_salaries.map(
        (role, index) =>
          index === roleIndex
            ? { ...role, average_salary: parseFloat(newSalary) }
            : role,
      );

      setLocalValues((prev) => ({
        ...prev,
        bim_role_salaries: updatedRoleSalaries,
      }));

      // Подготавливаем данные для калькуляции
      const updatedBimData = {
        include_bonuses: localValues.include_bonuses,
        // Используем ставку из измененной роли, а не всегда первую
        average_salary:
          updatedRoleSalaries[roleIndex]?.average_salary ||
          getSectionData("bim_coordination")?.average_salary,
        quality_control_percent: localValues.quality_control_percent,
        direct_bonus_percent: localValues.direct_bonus_percent,
        bim_role_salaries: updatedRoleSalaries,
      };

      // Вызываем калькуляцию
      await calculateBimData(updatedBimData);

      setEditingFields((prev) => ({
        ...prev,
        [`role_salary_${roleIndex}`]: false,
      }));
    } catch (error) {
    } finally {
      setSavingFields((prev) => ({
        ...prev,
        [`role_salary_${roleIndex}`]: false,
      }));
    }
  };

  // Обработчик для отмены изменений в ставке специалиста
  const handleCancelRoleSalary = (roleIndex) => {
    setEditingFields((prev) => ({
      ...prev,
      [`role_salary_${roleIndex}`]: false,
    }));
    // Восстанавливаем исходное значение
    setLocalValues((prev) => ({
      ...prev,
      bim_role_salaries: prev.bim_role_salaries.map((role, index) =>
        index === roleIndex
          ? {
              ...role,
              average_salary:
                bimCoordinationData.bim_role_salaries?.[index]
                  ?.average_salary || role.average_salary,
            }
          : role,
      ),
    }));
  };

  const handleSaveField = async (fieldName, value) => {
    setSavingFields((prev) => ({ ...prev, [fieldName]: true }));

    try {
      // Обновляем значение в локальном состоянии
      setLocalValues((prev) => ({ ...prev, [fieldName]: value }));

      // Подготавливаем данные для калькуляции
      const updatedBimData = {
        include_bonuses: localValues.include_bonuses,
        average_salary:
          localValues.bim_role_salaries?.[0]?.average_salary ||
          getSectionData("bim_coordination")?.average_salary,
        quality_control_percent:
          fieldName === "quality_control_percent"
            ? parseFloat(value)
            : localValues.quality_control_percent,
        direct_bonus_percent:
          fieldName === "direct_bonus_percent"
            ? parseFloat(value)
            : localValues.direct_bonus_percent,
        bim_role_salaries: localValues.bim_role_salaries,
      };

      // Вызываем калькуляцию
      await calculateBimData(updatedBimData);

      setEditingFields((prev) => ({ ...prev, [fieldName]: false }));
    } catch (error) {
    } finally {
      setSavingFields((prev) => ({ ...prev, [fieldName]: false }));
    }
  };

  const handleCancelEdit = (fieldName) => {
    setEditingFields((prev) => ({ ...prev, [fieldName]: false }));
    setLocalValues((prev) => ({
      ...prev,
      [fieldName]:
        fieldName === "quality_control_percent"
          ? qualityControlPercent
          : directBonusPercent,
    }));
  };

  const handleAddBimSection = () => {
    setSelectedBimSections([]);
    setBimSectionSearch("");
    setShowAddBimSectionModal(true);
  };

  const handleCancelAddBimSection = () => {
    setShowAddBimSectionModal(false);
    setSelectedBimSections([]);
    setBimSectionSearch("");
  };

  const handleConfirmAddBimSection = async () => {
    if (selectedBimSections.length === 0) return;
    const toAdd = workTabSections.filter((s) =>
      selectedBimSections.includes(s.id),
    );
    if (toAdd.length === 0) return;

    const newRoleObjects = toAdd.map((s) => ({
      role: s.name,
      average_salary: 0,
      section_id: s.id,
      day_efforts: { direct: 0, bonus: 0 },
      money_expenditures: { direct: 0, bonus: 0 },
    }));
    const newRoles = [...displayRoles, ...newRoleObjects];
    const newBimSalaries = newRoles.map((r) => ({
      role: r.role,
      average_salary: r.average_salary != null ? r.average_salary : 0,
      section_id: r.section_id ?? null,
    }));

    setLocalValues((prev) => ({
      ...prev,
      bim_role_salaries: newBimSalaries,
    }));
    const newEstimateData = {
      ...estimateData,
      bim_coordination: {
        ...estimateData?.bim_coordination,
        roles: newRoles,
      },
    };
    setEstimateData(newEstimateData);

    try {
      const updatedBimData = {
        include_bonuses: localValues.include_bonuses,
        average_salary:
          localValues.bim_role_salaries?.[0]?.average_salary ||
          getSectionData("bim_coordination")?.average_salary,
        quality_control_percent: localValues.quality_control_percent,
        direct_bonus_percent: localValues.direct_bonus_percent,
        bim_role_salaries: newBimSalaries,
      };
      await calculateBimData(updatedBimData, newEstimateData);
    } catch (e) {
      console.error(e);
    }

    setShowAddBimSectionModal(false);
    setSelectedBimSections([]);
    setBimSectionSearch("");
  };

  const handleRemoveBimColumn = async (roleIndex) => {
    if (roleIndex === 0) return;
    const newRoles = displayRoles.filter((_, i) => i !== roleIndex);
    const newBimSalaries = newRoles.map((r) => ({
      role: r.role,
      average_salary: r.average_salary != null ? r.average_salary : 0,
      section_id: r.section_id ?? null,
    }));
    setLocalValues((prev) => ({
      ...prev,
      bim_role_salaries: newBimSalaries,
    }));
    const newEstimateData = {
      ...estimateData,
      bim_coordination: {
        ...estimateData?.bim_coordination,
        roles: newRoles,
      },
    };
    setEstimateData(newEstimateData);
    try {
      const updatedBimData = {
        include_bonuses: localValues.include_bonuses,
        average_salary:
          localValues.bim_role_salaries?.[0]?.average_salary ||
          getSectionData("bim_coordination")?.average_salary,
        quality_control_percent: localValues.quality_control_percent,
        direct_bonus_percent: localValues.direct_bonus_percent,
        bim_role_salaries: newBimSalaries,
      };
      await calculateBimData(updatedBimData, newEstimateData);
    } catch (e) {
      console.error(e);
    }
  };

  const [bimDragOverCol, setBimDragOverCol] = useState(null);

  const reorderArray = (arr, fromIndex, toIndex) => {
    if (fromIndex === toIndex) return arr;
    const list = [...arr];
    const [removed] = list.splice(fromIndex, 1);
    list.splice(toIndex, 0, removed);
    return list;
  };

  const handleReorderBimColumns = async (fromIndex, toIndex) => {
    if (fromIndex === toIndex) return;
    const newRoles = reorderArray(displayRoles, fromIndex, toIndex);
    const newBimSalaries = reorderArray(
      localValues.bim_role_salaries ?? [],
      fromIndex,
      toIndex,
    );
    setLocalValues((prev) => ({ ...prev, bim_role_salaries: newBimSalaries }));
    const newEstimateData = {
      ...estimateData,
      bim_coordination: {
        ...estimateData?.bim_coordination,
        roles: newRoles,
      },
    };
    setEstimateData(newEstimateData);
    try {
      const updatedBimData = {
        include_bonuses: localValues.include_bonuses,
        average_salary:
          newBimSalaries[0]?.average_salary ||
          getSectionData("bim_coordination")?.average_salary,
        quality_control_percent: localValues.quality_control_percent,
        direct_bonus_percent: localValues.direct_bonus_percent,
        bim_role_salaries: newBimSalaries,
      };
      await calculateBimData(updatedBimData, newEstimateData);
    } catch (e) {
      console.error(e);
    }
  };

  // Компонент для редактируемого поля
  const EditableField = ({
    fieldName,
    value,
    isCurrency = false,
    isPercent = false,
    onSave,
    onCancel,
    isEditing,
    onEdit,
    isSaving,
  }) => {
    const [localValue, setLocalValue] = useState(value);
    const inputRef = React.useRef(null);
    const computeWidthPx = (val) => {
      const text =
        val === undefined || val === null || val === "" ? "0" : String(val);
      const charCount = text.length;
      return Math.max(30, charCount * 10 + 16);
    };

    // Обновляем локальное значение при изменении props
    useEffect(() => {
      setLocalValue(value);
    }, [value]);

    useEffect(() => {
      if (isEditing && inputRef.current) {
        inputRef.current.focus();
        const input = inputRef.current;
        requestAnimationFrame(() => {
          try {
            input.select();
          } catch {}
        });
      }
    }, [isEditing]);

    const currentValue = isEditing ? localValue : value;

    if (isEditing) {
      const w = computeWidthPx(currentValue);
      return (
        <div
          style={{ position: "relative", width: `${w}px`, margin: "0 auto" }}
        >
          <input
            type="number"
            step={isPercent ? "0.01" : "1"}
            value={currentValue}
            ref={inputRef}
            onChange={(e) => setLocalValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                onSave(fieldName, localValue);
              } else if (e.key === "Escape") {
                setLocalValue(value);
                onCancel(fieldName);
              }
            }}
            style={{
              width: `${w}px`,
              padding: "6px 10px",
              border: "1.5px dashed #B8C2D1",
              borderRadius: "6px",
              background: "#FFFFFF",
              fontSize: "14px",
              textAlign: "center",
              outline: "none",
              transition: "border-color 0.2s",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#007bff")}
            onBlur={(e) => {
              e.target.style.borderColor = "#ccc";
              // Автоматически сохраняем при потере фокуса
              onSave(fieldName, localValue);
            }}
            disabled={!canEditBase || isSaving}
          />
        </div>
      );
    }

    const displayValue = isCurrency
      ? `₸ ${Number(value).toLocaleString()}`
      : isPercent
        ? `${value}`
        : value;
    const wView = computeWidthPx(displayValue);

    return (
      <div
        style={{ width: `${wView}px`, margin: "0 auto", textAlign: "center" }}
      >
        <span
          style={{
            fontSize: "16px",
            display: "inline-block",
            width: `${wView}px`,
            padding: "6px 10px",
            border: "1.5px dashed #B8C2D1",
            borderRadius: "6px",
            background: !canEditBase ? "#F4F6FA" : "#F9FBFF",
            color: "#1F2937",
            cursor: !canEditBase ? "default" : "pointer",
          }}
          onClick={() => (!canEditBase ? undefined : onEdit(fieldName, value))}
        >
          {displayValue}
        </span>
      </div>
    );
  };

  // Компонент для редактируемой средней ставки специалиста
  const EditableRoleSalary = ({
    roleIndex,
    role,
    onSave,
    onCancel,
    isEditing,
    onEdit,
    isSaving,
  }) => {
    const [localValue, setLocalValue] = useState(role.average_salary);
    const inputRef = React.useRef(null);
    const computeWidthPx = (val) => {
      const text =
        val === undefined || val === null || val === "" ? "0" : String(val);
      const charCount = text.length;
      return Math.max(30, charCount * 10 + 16);
    };

    useEffect(() => {
      setLocalValue(role.average_salary);
    }, [role.average_salary]);

    useEffect(() => {
      if (isEditing && inputRef.current) {
        inputRef.current.focus();
        const input = inputRef.current;
        requestAnimationFrame(() => {
          try {
            input.select();
          } catch {}
        });
      }
    }, [isEditing]);

    const currentValue = isEditing ? localValue : role.average_salary;

    if (isEditing) {
      const w = computeWidthPx(currentValue);
      return (
        <div
          style={{ position: "relative", width: `${w}px`, margin: "0 auto" }}
        >
          <input
            type="number"
            step="1000"
            value={currentValue}
            ref={inputRef}
            onChange={(e) => setLocalValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                onSave(roleIndex, localValue);
              } else if (e.key === "Escape") {
                setLocalValue(role.average_salary);
                onCancel(roleIndex);
              }
            }}
            style={{
              width: `${w}px`,
              padding: "6px 10px",
              border: "1.5px dashed #B8C2D1",
              borderRadius: "6px",
              background: "#FFFFFF",
              fontSize: "14px",
              textAlign: "center",
              outline: "none",
              transition: "border-color 0.2s",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#007bff")}
            onBlur={(e) => {
              e.target.style.borderColor = "#ccc";
              // Автоматически сохраняем при потере фокуса
              onSave(roleIndex, localValue);
            }}
            disabled={!canEditBase || isSaving}
          />
        </div>
      );
    }

    const displayValue = `₸ ${Number(role.average_salary).toLocaleString()}`;
    const wView = computeWidthPx(displayValue);

    return (
      <div
        style={{ width: `${wView}px`, margin: "0 auto", textAlign: "center" }}
        onClick={() =>
          !canEditBase ? undefined : onEdit(roleIndex, role.average_salary)
        }
      >
        <span
          style={{
            fontSize: "16px",
            display: "inline-block",
            width: `${wView}px`,
            padding: "6px 10px",
            border: "1.5px dashed #B8C2D1",
            borderRadius: "6px",
            background: !canEditBase ? "#F4F6FA" : "#F9FBFF",
            color: "#1F2937",
            cursor: !canEditBase ? "default" : "pointer",
          }}
        >
          {displayValue}
        </span>
      </div>
    );
  };

  // Рассчитываем общую сумму из данных API
  const totalDirect = bimCoordinationData.total_money_expenditures?.direct;
  const totalBonus = bimCoordinationData.total_money_expenditures?.bonus;
  const total = bimCoordinationData.total;
  const bimFooter = getModuleCorrectionFooter(corrections, "bim", roles, {
    direct: totalDirect,
    bonus: totalBonus,
    total,
  });

  // Экспортируем функции через ref
  useImperativeHandle(ref, () => ({
    getTabData: () => {
      return {
        bim_coordination: {
          include_bonuses: localValues.include_bonuses,
          quality_control_percent: localValues.quality_control_percent,
          direct_bonus_percent: localValues.direct_bonus_percent,
          bim_role_salaries: localValues.bim_role_salaries,
        },
      };
    },
    triggerCalculation: async () => {
      try {
        // Подготавливаем данные для калькуляции
        const updatedBimData = {
          include_bonuses: localValues.include_bonuses,
          average_salary:
            localValues.bim_role_salaries?.[0]?.average_salary ||
            getSectionData("bim_coordination")?.average_salary,
          quality_control_percent: localValues.quality_control_percent,
          direct_bonus_percent: localValues.direct_bonus_percent,
          bim_role_salaries: localValues.bim_role_salaries,
        };

        // Вызываем калькуляцию
        const calculationResult = await calculateBimData(updatedBimData);
        return calculationResult;
      } catch (error) {
        throw error;
      }
    },
    handleSaveForEstimate: async () => {
      try {
        // Используем функцию сохранения из контекста
        const saveResult = await performSave(estimateContext.projectId);

        return saveResult;
      } catch (error) {
        throw error;
      }
    },
  }));

  return (
    <div className="bim-table-block">
      {/* Чекбокс "Учитывать бонусы при расчете" */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "16px",
          padding: "16px",
          backgroundColor: "#f8f9fa",
          borderRadius: "8px",
          border: "1px solid #e9ecef",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
          <label style={{ fontSize: "14px", fontWeight: "600", color: "#333" }}>
            Учитывать бонусы при расчете:
          </label>
          <label className="switch">
            <input
              type="checkbox"
              checked={localValues.include_bonuses}
              onChange={(e) =>
                handleCheckboxChange("include_bonuses", e.target.checked)
              }
              disabled={!canEditBase}
            />
            <span className="slider"></span>
          </label>
        </div>
        <button
          onClick={handleAddBimSection}
          disabled={!canEditBase}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "8px 16px",
            backgroundColor: "#fff",
            color: "#2A5E38",
            border: "none",
            borderRadius: "4px",
            cursor: !canEditBase ? "not-allowed" : "pointer",
            opacity: !canEditBase ? 0.6 : 1,
            fontSize: "14px",
            fontWeight: "500",
          }}
        >
          <span style={{ fontSize: "16px", fontWeight: "bold" }}>+</span>
          Добавить раздел
        </button>
      </div>

      <div className="bim-title-row">
        <div className="bim-title-block">
          <div className="bim-title">% доля BIM координирования</div>
          <EditableField
            fieldName="quality_control_percent"
            value={localValues.quality_control_percent}
            isPercent={true}
            onSave={handleSaveField}
            onCancel={handleCancelEdit}
            isEditing={editingFields.quality_control_percent}
            onEdit={handleEditField}
            isSaving={savingFields.quality_control_percent}
          />
        </div>
        <div className="bim-title-block">
          <div className="bim-title">% доля бонусов от ПЗ</div>
          <EditableField
            fieldName="direct_bonus_percent"
            value={localValues.direct_bonus_percent}
            isPercent={true}
            onSave={handleSaveField}
            onCancel={handleCancelEdit}
            isEditing={editingFields.direct_bonus_percent}
            onEdit={handleEditField}
            isSaving={savingFields.direct_bonus_percent}
          />
        </div>
      </div>

      <div className="bim-table-mock">
        {bimColumns.length > 0 ? (
          <table>
            <thead>
              <tr>
                <th></th>
                {bimColumns.map((col, i) => {
                  const isBimManagerCol = i === 0;
                  const canDrag = canEditBase && !loading && !isBimManagerCol;
                  return (
                  <th
                    key={i}
                    draggable={canDrag}
                    onDragStart={canDrag ? (e) => {
                      if (e.dataTransfer) {
                        e.dataTransfer.effectAllowed = "move";
                        e.dataTransfer.setData("text/plain", String(i));
                      }
                    } : undefined}
                    onDragOver={(e) => {
                      e.preventDefault();
                      if (e.dataTransfer) e.dataTransfer.dropEffect = "move";
                      if (canDrag) setBimDragOverCol(i);
                    }}
                    onDragLeave={() => setBimDragOverCol(null)}
                    onDrop={(e) => {
                      e.preventDefault();
                      setBimDragOverCol(null);
                      if (i === 0) return;
                      const fromStr = e.dataTransfer?.getData("text/plain");
                      if (fromStr == null) return;
                      const fromIndex = parseInt(fromStr, 10);
                      if (Number.isNaN(fromIndex) || fromIndex === i) return;
                      handleReorderBimColumns(fromIndex, i);
                    }}
                    onDragEnd={() => setBimDragOverCol(null)}
                    style={{
                      cursor: canDrag ? "grab" : "default",
                      backgroundColor: bimDragOverCol === i ? "rgba(42, 94, 56, 0.12)" : undefined,
                      transition: "background-color 0.15s ease",
                    }}
                  >
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "6px",
                      }}
                    >
                      {canDrag && (
                        <span
                          title="Перетащите для изменения порядка"
                          style={{
                            cursor: "grab",
                            color: "#888",
                            fontSize: "14px",
                            userSelect: "none",
                          }}
                          onMouseDown={(ev) => ev.stopPropagation()}
                        >
                          ⋮⋮
                        </span>
                      )}
                      <span>{col}</span>
                      {i > 0 && canEditBase && (
                        <button
                          type="button"
                          onClick={() => handleRemoveBimColumn(i)}
                          disabled={loading}
                          title="Удалить раздел"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            justifyContent: "center",
                            width: "24px",
                            height: "24px",
                            padding: 0,
                            background: "none",
                            border: "none",
                            borderRadius: "4px",
                            cursor: loading ? "not-allowed" : "pointer",
                            color: "#dc3545",
                            fontSize: "18px",
                            lineHeight: 1,
                            flexShrink: 0,
                          }}
                        >
                          ×
                        </button>
                      )}
                    </div>
                  </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {/* Строка "Средняя ставка" с индивидуальными значениями */}
              <tr>
                <td>Средняя ставка</td>
                {localValues.bim_role_salaries.map((role, index) => (
                  <td key={index} style={{ textAlign: "center" }}>
                    <EditableRoleSalary
                      roleIndex={index}
                      role={role}
                      onSave={handleSaveRoleSalary}
                      onCancel={handleCancelRoleSalary}
                      isEditing={editingFields[`role_salary_${index}`]}
                      onEdit={(roleIndex, value) =>
                        setEditingFields((prev) => ({
                          ...prev,
                          [`role_salary_${roleIndex}`]: true,
                        }))
                      }
                      isSaving={savingFields[`role_salary_${index}`]}
                    />
                  </td>
                ))}
              </tr>
              <tr>
                <td>
                  {isCorrectionMode
                    ? "Трудозатраты базовые (дн)"
                    : "Трудозатраты (дн)"}
                </td>
                {roles.map((role, i) => (
                  <td key={i} style={{ textAlign: "center" }}>
                    {(role.day_efforts?.direct ?? 0).toFixed(1)}
                  </td>
                ))}
              </tr>
              <ModuleCorrectionRows
                module="bim"
                roles={roles}
                corrections={corrections}
                onChange={updateModuleCorrectionValue}
                editable={canEditCorrections}
              />
              <tr>
                <td>Бонусы в днях (дн)</td>
                <ModuleBonusDayCells
                  module="bim"
                  roles={roles}
                  corrections={corrections}
                />
              </tr>
              <tr>
                <td>Прямые затраты (тг)</td>
                <ModuleDirectMoneyCells
                  module="bim"
                  roles={roles}
                  corrections={corrections}
                />
              </tr>
              <tr>
                <td>Бонусы в деньгах (тг)</td>
                <ModuleBonusMoneyCells
                  module="bim"
                  roles={roles}
                  corrections={corrections}
                />
              </tr>
            </tbody>
          </table>
        ) : (
          <div
            style={{
              textAlign: "center",
              padding: "40px",
              color: "#666",
              fontSize: "16px",
            }}
          >
            {loading ? "Выполняется калькуляция..." : "Загрузка данных..."}
          </div>
        )}
      </div>
      <div className="bim-table-footer">
        <div className="bim-footer-main">
          <div className="bim-footer-label">ВСЕ ЗАТРАТЫ</div>
          <div
            className={`bim-footer-sum main${
              bimFooter.changed ? " correction-sum-changed" : ""
            }`}
          >
            ₸ {bimFooter.total.toLocaleString()}
            {bimFooter.changed ? (
              <span className="correction-sum-delta">
                {formatSignedDelta(bimFooter.extraMoney, { money: true })}
              </span>
            ) : null}
          </div>
        </div>
        <div className="bim-footer-right">
          <div className="bim-footer-block">
            <div className="bim-footer-label">Прямые затраты</div>
            <div
              className={`bim-footer-sum${
                bimFooter.extra.money_expenditures ? " correction-sum-changed" : ""
              }`}
            >
              ₸ {bimFooter.totalDirect.toLocaleString()}
            </div>
          </div>
          <div className="bim-footer-block">
            <div className="bim-footer-label">Бонусы</div>
            <div
              className={`bim-footer-sum${
                bimFooter.extra.bonus_money_expenditures
                  ? " correction-sum-changed"
                  : ""
              }`}
            >
              ₸ {bimFooter.totalBonus.toLocaleString()}
            </div>
          </div>
        </div>
      </div>

      {/* Модалка: только разделы из Рабочего проектирования */}
      {showAddBimSectionModal && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1000,
          }}
          onClick={handleCancelAddBimSection}
        >
          <div
            className="add-section-modal"
            style={{
              backgroundColor: "white",
              borderRadius: "8px",
              padding: "24px",
              minWidth: "400px",
              maxWidth: "500px",
              boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h3
              style={{
                margin: "0 0 20px 0",
                fontSize: "18px",
                fontWeight: "600",
                color: "#333",
              }}
            >
              Добавление раздела в BIM координирование
            </h3>
            <p
              style={{
                margin: "0 0 12px 0",
                fontSize: "13px",
                color: "#666",
              }}
            >
              Доступны только разделы, добавленные на вкладке «Рабочее
              проектирование».
            </p>
            <div style={{ marginBottom: "16px" }}>
              <label
                style={{
                  display: "block",
                  marginBottom: "8px",
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#333",
                }}
              >
                Выберите раздел из списка*
              </label>
              <input
                type="text"
                placeholder="Поиск"
                value={bimSectionSearch}
                onChange={(e) => setBimSectionSearch(e.target.value)}
                style={{
                  width: "100%",
                  padding: "8px 12px",
                  marginBottom: "10px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  fontSize: "14px",
                  boxSizing: "border-box",
                }}
              />
              <div
                style={{
                  maxHeight: "240px",
                  overflowY: "auto",
                  border: "1px solid #e0e0e0",
                  borderRadius: "4px",
                  padding: "8px",
                  backgroundColor: "#fafafa",
                }}
              >
                {workTabSections.length === 0 ? (
                  <div
                    style={{
                      padding: "16px",
                      color: "#666",
                      fontSize: "14px",
                    }}
                  >
                    Нет разделов с вкладки «Рабочее проектирование». Добавьте
                    разделы там сначала.
                  </div>
                ) : (
                  (() => {
                    const searchLower = (bimSectionSearch || "")
                      .toLowerCase()
                      .trim();
                    const filtered = searchLower
                      ? workTabSections.filter((s) =>
                          (s.name || "").toLowerCase().includes(searchLower),
                        )
                      : workTabSections;
                    const alreadyInBim = new Set(
                      displayRoles.map((r) => r.role),
                    );
                    const available = filtered.filter(
                      (s) => !alreadyInBim.has(s.name),
                    );
                    if (available.length === 0) {
                      return (
                        <div
                          style={{
                            padding: "16px",
                            color: "#666",
                            fontSize: "14px",
                          }}
                        >
                          {filtered.length === 0
                            ? "Ничего не найдено"
                            : "Все эти разделы уже добавлены в BIM."}
                        </div>
                      );
                    }
                    return available.map((section) => {
                      const checked = selectedBimSections.includes(section.id);
                      return (
                        <label
                          key={section.id}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            padding: "8px 6px",
                            cursor: "pointer",
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={(e) => {
                              setSelectedBimSections((prev) =>
                                e.target.checked
                                  ? [...prev, section.id]
                                  : prev.filter((x) => x !== section.id),
                              );
                            }}
                            style={{
                              width: "16px",
                              height: "16px",
                              cursor: "pointer",
                            }}
                          />
                          <span className="section-label-text">
                            <span className="section-label-name">{section.name}</span>
                            {section.description && (
                              <span className="section-label-description">
                                {section.description}
                              </span>
                            )}
                          </span>
                        </label>
                      );
                    });
                  })()
                )}
              </div>
            </div>
            <div
              style={{
                display: "flex",
                gap: "12px",
                justifyContent: "flex-end",
                marginTop: "24px",
              }}
            >
              <button
                onClick={handleCancelAddBimSection}
                style={{
                  padding: "8px 16px",
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  backgroundColor: "white",
                  color: "#666",
                  cursor: "pointer",
                  fontSize: "14px",
                }}
              >
                Отмена
              </button>
              <button
                onClick={handleConfirmAddBimSection}
                disabled={selectedBimSections.length === 0}
                style={{
                  padding: "8px 16px",
                  border: "none",
                  borderRadius: "4px",
                  backgroundColor:
                    selectedBimSections.length > 0 ? "#2A5E38" : "#ccc",
                  color: "white",
                  cursor:
                    selectedBimSections.length > 0 ? "pointer" : "not-allowed",
                  fontSize: "14px",
                  opacity: selectedBimSections.length > 0 ? 1 : 0.6,
                }}
              >
                Добавить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default BimTab;
